import React, { useState, useEffect } from 'react';
import { Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const DurationPicker = ({ value = '00:00:00', onChange, maxDuration = '23:59:59', label = 'Duration' }) => {
	const [hours, setHours] = useState('00');
	const [minutes, setMinutes] = useState('00');
	const [seconds, setSeconds] = useState('00');

	useEffect(() => {
		if (value) {
			const parts = value.split(':');
			setHours(parts[0] || '00');
			setMinutes(parts[1] || '00');
			setSeconds(parts[2] || '00');
		}
	}, [value]);

	const getMaxValues = () => {
		const [maxH, maxM, maxS] = maxDuration.split(':').map(Number);
		return { maxH, maxM, maxS };
	};

	const { maxH, maxM, maxS } = getMaxValues();

	const generateOptions = (max, current, type) => {
		const options = [];
		const currentH = Number(hours);
		const currentM = Number(minutes);

		let limit = max;

		// If we're not at the max hour, allow full range for minutes/seconds
		if (type === 'minutes' && currentH < maxH) {
			limit = 59;
		} else if (type === 'seconds' && (currentH < maxH || currentM < maxM)) {
			limit = 59;
		}

		for (let i = 0; i <= limit; i++) {
			options.push(String(i).padStart(2, '0'));
		}
		return options;
	};

	const handleChange = (type, val) => {
		let newH = hours;
		let newM = minutes;
		let newS = seconds;

		if (type === 'hours') {
			newH = val;
			// If new hour exceeds max, reset minutes and seconds
			if (Number(val) >= maxH) {
				newM = String(Math.min(Number(minutes), maxM)).padStart(2, '0');
				newS = String(Math.min(Number(seconds), maxS)).padStart(2, '0');
			}
			setHours(newH);
			setMinutes(newM);
			setSeconds(newS);
		} else if (type === 'minutes') {
			newM = val;
			// If at max hour and new minute exceeds max, reset seconds
			if (Number(newH) === maxH && Number(val) >= maxM) {
				newS = String(Math.min(Number(seconds), maxS)).padStart(2, '0');
			}
			setMinutes(newM);
			setSeconds(newS);
		} else if (type === 'seconds') {
			newS = val;
			setSeconds(newS);
		}

		// Call onChange with formatted time
		const formatted = `${newH}:${newM}:${newS}`;
		onChange(formatted);
	};

	return (
		<Box>
			<InputLabel sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>{label}</InputLabel>
			<Box display="flex" gap={1} alignItems="center">
				<FormControl fullWidth size="small">
					<Select value={hours} onChange={e => handleChange('hours', e.target.value)} displayEmpty>
						{generateOptions(maxH, hours, 'hours').map(h => (
							<MenuItem key={h} value={h}>
								{h}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box sx={{ color: 'text.secondary', fontWeight: 'bold' }}>:</Box>

				<FormControl fullWidth size="small">
					<Select value={minutes} onChange={e => handleChange('minutes', e.target.value)} displayEmpty>
						{generateOptions(maxM, minutes, 'minutes').map(m => (
							<MenuItem key={m} value={m}>
								{m}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Box sx={{ color: 'text.secondary', fontWeight: 'bold' }}>:</Box>

				<FormControl fullWidth size="small">
					<Select value={seconds} onChange={e => handleChange('seconds', e.target.value)} displayEmpty>
						{generateOptions(maxS, seconds, 'seconds').map(s => (
							<MenuItem key={s} value={s}>
								{s}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>
			<Box sx={{ mt: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>HH : MM : SS</Box>
		</Box>
	);
};

export default DurationPicker;
